import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon,
  MapPin, Clock, Users, Eye, EyeOff, Trash2, Check, X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

// ---------------------------------------------------------------------------
// Event-type color tokens. One source of truth for both the dot/chip on the
// month grid and the badge in the detail panel.
// ---------------------------------------------------------------------------
const TYPE_TONE = {
  meeting:     { bg: "bg-cyan-300/15",    fg: "text-cyan-200",    dot: "bg-cyan-300",    border: "border-cyan-300/30" },
  family_call: { bg: "bg-cyan-300/15",    fg: "text-cyan-200",    dot: "bg-cyan-300",    border: "border-cyan-300/30" },
  visit:       { bg: "bg-rose-400/15",    fg: "text-rose-200",    dot: "bg-rose-400",    border: "border-rose-400/30" },
  combine:     { bg: "bg-amber-300/15",   fg: "text-amber-200",   dot: "bg-amber-300",   border: "border-amber-300/30" },
  pro_day:     { bg: "bg-amber-300/15",   fg: "text-amber-200",   dot: "bg-amber-300",   border: "border-amber-300/30" },
  interview:   { bg: "bg-violet-300/15",  fg: "text-violet-200",  dot: "bg-violet-300",  border: "border-violet-300/30" },
  training:    { bg: "bg-emerald-300/15", fg: "text-emerald-200", dot: "bg-emerald-300", border: "border-emerald-300/30" },
  media:       { bg: "bg-blue-300/15",    fg: "text-blue-200",    dot: "bg-blue-300",    border: "border-blue-300/30" },
  personal:    { bg: "bg-slate-300/10",   fg: "text-slate-200",   dot: "bg-slate-400",   border: "border-slate-300/20" },
  other:       { bg: "bg-white/5",        fg: "text-1",           dot: "bg-white/40",    border: "border-white/15" },
};

const TYPE_LABEL = {
  meeting: "Meeting",
  family_call: "Family Call",
  visit: "Team Visit",
  combine: "Combine",
  pro_day: "Pro Day",
  interview: "Interview",
  training: "Training",
  media: "Media",
  personal: "Personal",
  other: "Other",
};

// ---------------------------------------------------------------------------
// Calendar math helpers — pure JS, no date library required.
// ---------------------------------------------------------------------------
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABEL = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function startOfGrid(d) {
  const s = startOfMonth(d);
  s.setDate(s.getDate() - s.getDay()); // back up to Sunday
  return s;
}
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

// ---------------------------------------------------------------------------
// CalendarPage — single source for both admin (`/app/calendar`) and family
// (same route, scoped server-side). Admin gets an athlete filter + visibility
// toggle on each event; family gets accept/decline.
// ---------------------------------------------------------------------------
export default function CalendarPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [playerFilter, setPlayerFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  // Inspection / creation state
  const [dayOpen, setDayOpen] = useState(null); // Date or null
  const [editing, setEditing] = useState(null); // event or "new"

  const monthLabel = `${MONTH_LABEL[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const gridStart = useMemo(() => startOfGrid(cursor), [cursor]);
  const days = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)), [gridStart]);

  const refresh = useCallback(async () => {
    const start = days[0].toISOString();
    const end = addDays(days[41], 1).toISOString();
    const params = { start, end };
    if (playerFilter !== "all") params.player_id = playerFilter;
    const r = await api.get("/events", { params });
    setEvents(r.data);
  }, [days, playerFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/players").then((r) => setPlayers(r.data)).catch(() => setPlayers([])),
      refresh(),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, playerFilter]);

  // Group events by ISO yyyy-mm-dd for fast lookup in each day cell.
  const byDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const k = new Date(e.start_at).toDateString();
      (map[k] = map[k] || []).push(e);
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.start_at.localeCompare(b.start_at)));
    return map;
  }, [events]);

  const playerById = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);

  const today = new Date();

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => setCursor(startOfMonth(new Date()));

  const handleSaved = async (msg) => {
    toast.success(msg);
    setEditing(null);
    await refresh();
  };
  const handleDelete = async (id) => {
    if (!confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Event removed");
    setEditing(null);
    setDayOpen(null);
    await refresh();
  };

  // Filtered athlete options for admin filter dropdown.
  const filterPlayers = isAdmin ? players : players.filter((p) => true);

  return (
    <div className="max-w-[1500px] mx-auto" data-testid="calendar-page">
      <PageHeader
        title="Calendar"
        subtitle={isAdmin ? "Every athlete on one canvas. Family-shared events glow cyan." : "Your athlete's schedule, shared with the OmniDraftLab team."}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <Select value={playerFilter} onValueChange={setPlayerFilter}>
                <SelectTrigger data-testid="cal-player-filter" className="w-[200px] bg-white/5 border-white/10 text-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-strong border-white/10 max-h-72">
                  <SelectItem value="all" data-testid="cal-player-filter-all">All athletes</SelectItem>
                  {filterPlayers.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`cal-player-filter-${p.id}`}>{p.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button data-testid="cal-new-event" onClick={() => setEditing("new")} className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold rounded-xl">
              <Plus className="h-4 w-4 mr-1" /> New event
            </Button>
          </div>
        }
      />

      {/* Month nav row */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={goPrev} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="text-1 text-xl font-semibold tracking-tight min-w-[200px] text-center" data-testid="cal-month-label">{monthLabel}</div>
          <Button variant="ghost" size="icon" onClick={goNext} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" onClick={goToday} className="text-2 text-xs" data-testid="cal-today">Today</Button>
        </div>
        {loading && <div className="text-3 text-xs">Loading…</div>}
      </div>

      {/* Day-of-week header */}
      <div className="mt-4 grid grid-cols-7 gap-1 text-3 text-[10px] uppercase tracking-[0.2em] font-medium">
        {DOW.map((d) => <div key={d} className="px-2 py-1">{d}</div>)}
      </div>

      {/* Month grid */}
      <div className="mt-1 grid grid-cols-7 gap-1">
        {days.map((d, idx) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, today);
          const dayEvents = byDay[d.toDateString()] || [];
          const visible = dayEvents.slice(0, 3);
          const overflow = dayEvents.length - visible.length;
          return (
            <button
              key={idx}
              type="button"
              data-testid={`cal-day-${d.toISOString().slice(0, 10)}`}
              onClick={() => setDayOpen(d)}
              className={`min-h-[110px] rounded-xl p-2 text-left flex flex-col gap-1 border transition ${
                inMonth ? "bg-white/[0.03] border-white/8 hover:bg-white/[0.06]" : "bg-white/[0.01] border-white/5 text-3"
              } ${isToday ? "ring-1 ring-cyan-300/60 ring-offset-0" : ""}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold tabular-nums ${isToday ? "text-cyan-200" : inMonth ? "text-1" : "text-3"}`}>
                  {d.getDate()}
                </span>
                {dayEvents.length > 0 && <span className="text-3 text-[9px]">{dayEvents.length}</span>}
              </div>
              <div className="flex flex-col gap-1 overflow-hidden">
                {visible.map((e) => {
                  const tone = TYPE_TONE[e.event_type] || TYPE_TONE.other;
                  return (
                    <div
                      key={e.id}
                      onClick={(ev) => { ev.stopPropagation(); setEditing(e); }}
                      className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded-md ${tone.bg} ${tone.fg} text-[10px] truncate cursor-pointer ${!e.visible_to_family && isAdmin ? "border border-dashed border-white/20" : ""}`}
                      title={`${e.title} · ${fmtTime(e.start_at)}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot} shrink-0`} />
                      <span className="truncate">{fmtTime(e.start_at)} {e.title}</span>
                    </div>
                  );
                })}
                {overflow > 0 && <div className="text-3 text-[10px] pl-1.5">+ {overflow} more</div>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Day inspect drawer */}
      <Sheet open={!!dayOpen} onOpenChange={(o) => !o && setDayOpen(null)}>
        <SheetContent className="glass-strong border-white/10 text-1 w-full sm:max-w-[480px]">
          <SheetHeader>
            <SheetTitle className="text-1 text-lg">
              {dayOpen ? dayOpen.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : ""}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-5 space-y-2">
            {dayOpen && (byDay[dayOpen.toDateString()] || []).map((e) => {
              const tone = TYPE_TONE[e.event_type] || TYPE_TONE.other;
              return (
                <button key={e.id} type="button" onClick={() => { setEditing(e); }} className={`w-full text-left p-3 rounded-xl border ${tone.border} ${tone.bg} hover:bg-white/[0.07] transition`}>
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
                    <span className={`${tone.fg} text-xs uppercase tracking-[0.18em]`}>{TYPE_LABEL[e.event_type]}</span>
                    {!e.visible_to_family && isAdmin && <span className="ml-auto text-3 text-[10px] flex items-center gap-1"><EyeOff className="h-3 w-3" /> private</span>}
                  </div>
                  <div className="text-1 text-sm font-semibold mt-1">{e.title}</div>
                  <div className="text-2 text-xs mt-1 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTime(e.start_at)} – {fmtTime(e.end_at)}</span>
                    {e.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.location}</span>}
                  </div>
                  {playerById[e.player_id] && <div className="text-3 text-[10px] mt-1">{playerById[e.player_id].full_name}</div>}
                </button>
              );
            })}
            {dayOpen && (byDay[dayOpen.toDateString()] || []).length === 0 && (
              <div className="text-3 text-sm">No events on this day.</div>
            )}
            <Button onClick={() => { setEditing("new"); }} variant="outline" className="w-full mt-3 border-white/15 bg-white/[0.03] hover:bg-white/[0.08]">
              <Plus className="h-4 w-4 mr-1" /> Add event for this day
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Event create/edit dialog */}
      <EventDialog
        open={!!editing}
        editing={editing}
        defaultDate={dayOpen}
        defaultPlayerId={playerFilter !== "all" ? playerFilter : (players[0]?.id)}
        players={players}
        isAdmin={isAdmin}
        onClose={() => setEditing(null)}
        onSaved={(msg) => handleSaved(msg)}
        onDelete={handleDelete}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// EventDialog — create + edit + accept/decline + share toggle.
// ---------------------------------------------------------------------------
function EventDialog({ open, editing, defaultDate, defaultPlayerId, players, isAdmin, onClose, onSaved, onDelete }) {
  const isNew = editing === "new";
  const ev = !isNew ? editing : null;

  const initial = useMemo(() => {
    if (ev) {
      return {
        player_id: ev.player_id,
        title: ev.title,
        description: ev.description || "",
        location: ev.location || "",
        start_at_local: toLocalInput(ev.start_at),
        end_at_local: toLocalInput(ev.end_at),
        event_type: ev.event_type,
        visible_to_family: !!ev.visible_to_family,
        notes: ev.notes || "",
      };
    }
    const base = defaultDate ? new Date(defaultDate) : new Date();
    base.setHours(10, 0, 0, 0);
    const end = new Date(base); end.setHours(11, 0, 0, 0);
    return {
      player_id: defaultPlayerId || "",
      title: "",
      description: "",
      location: "",
      start_at_local: toLocalInput(base.toISOString()),
      end_at_local: toLocalInput(end.toISOString()),
      event_type: "meeting",
      visible_to_family: false,
      notes: "",
    };
  }, [ev, defaultDate, defaultPlayerId]);

  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(initial); }, [initial]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!form.player_id) { toast.error("Athlete required"); return; }
    setBusy(true);
    try {
      const payload = {
        player_id: form.player_id,
        title: form.title.trim(),
        description: form.description || null,
        location: form.location || null,
        start_at: localInputToIso(form.start_at_local),
        end_at: localInputToIso(form.end_at_local),
        event_type: form.event_type,
        visible_to_family: isAdmin ? form.visible_to_family : true,
        notes: form.notes || null,
      };
      if (isNew) {
        await api.post("/events", payload);
        onSaved("Event created");
      } else {
        await api.patch(`/events/${ev.id}`, payload);
        onSaved("Event updated");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const respond = async (response) => {
    setBusy(true);
    try {
      await api.post(`/events/${ev.id}/respond`, { response });
      onSaved(response === "accepted" ? "Accepted" : "Declined");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const canEdit = isNew || isAdmin || (ev && ev.created_by_role === "family");
  const canRespond = !isNew && ev && ev.response === "pending" && (
    (isAdmin && ev.created_by_role === "family") ||
    (!isAdmin && ev.created_by_role === "admin")
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass-strong border-white/10 text-1 max-w-[560px]" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="text-1">{isNew ? "New event" : ev?.title}</DialogTitle>
          <DialogDescription className="text-3">
            {isNew ? "Schedule a new entry for the calendar." : `${TYPE_LABEL[ev?.event_type] || "Event"} · ${ev?.created_by_role === "admin" ? "Created by OmniDraftLab" : "Created by family"}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1 col-span-2">
              <Label className="text-2 text-xs">Title</Label>
              <Input data-testid="event-title" disabled={!canEdit} value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-white/5 border-white/10 text-1" placeholder="Family pre-combine call" />
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Athlete</Label>
              <Select value={form.player_id || undefined} onValueChange={(v) => set("player_id", v)} disabled={!canEdit || (!isAdmin && !isNew)}>
                <SelectTrigger data-testid="event-player" className="bg-white/5 border-white/10 text-1"><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10 max-h-72">
                  {players.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Type</Label>
              <Select value={form.event_type} onValueChange={(v) => set("event_type", v)} disabled={!canEdit}>
                <SelectTrigger data-testid="event-type" className="bg-white/5 border-white/10 text-1"><SelectValue /></SelectTrigger>
                <SelectContent className="glass-strong border-white/10 max-h-72">
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Starts</Label>
              <Input data-testid="event-start" type="datetime-local" disabled={!canEdit} value={form.start_at_local} onChange={(e) => set("start_at_local", e.target.value)} className="bg-white/5 border-white/10 text-1" />
            </div>
            <div className="space-y-1">
              <Label className="text-2 text-xs">Ends</Label>
              <Input data-testid="event-end" type="datetime-local" disabled={!canEdit} value={form.end_at_local} onChange={(e) => set("end_at_local", e.target.value)} className="bg-white/5 border-white/10 text-1" />
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-2 text-xs">Location</Label>
              <Input data-testid="event-location" disabled={!canEdit} value={form.location} onChange={(e) => set("location", e.target.value)} className="bg-white/5 border-white/10 text-1" placeholder="Phone · Indianapolis · home gym…" />
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-2 text-xs">Notes</Label>
              <Textarea data-testid="event-notes" disabled={!canEdit} value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="bg-white/5 border-white/10 text-1" />
            </div>
          </div>

          {isAdmin && (
            <div className="flex items-center justify-between p-3 rounded-xl border border-white/10 bg-white/[0.03]">
              <div>
                <div className="text-1 text-sm font-medium flex items-center gap-2"><Eye className="h-4 w-4 text-cyan-300" /> Share with family</div>
                <div className="text-3 text-[11px]">Off by default — toggle on to show this event in the family calendar.</div>
              </div>
              <Switch data-testid="event-visible-toggle" checked={form.visible_to_family} onCheckedChange={(v) => set("visible_to_family", v)} disabled={!canEdit} />
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:gap-2">
          {!isNew && canEdit && (
            <Button data-testid="event-delete" variant="ghost" onClick={() => onDelete(ev.id)} className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10 mr-auto">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          )}
          {canRespond && (
            <>
              <Button data-testid="event-decline" variant="outline" onClick={() => respond("declined")} disabled={busy} className="border-white/15 bg-white/[0.03] hover:bg-rose-500/10">
                <XIcon className="h-4 w-4 mr-1" /> Decline
              </Button>
              <Button data-testid="event-accept" onClick={() => respond("accepted")} disabled={busy} className="bg-emerald-300 text-[#04111A] hover:bg-emerald-200 font-semibold">
                <Check className="h-4 w-4 mr-1" /> Accept
              </Button>
            </>
          )}
          {canEdit && (
            <Button data-testid="event-save" onClick={save} disabled={busy} className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold">
              {isNew ? "Create event" : "Save changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
